import { useCallback, useState } from "react";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { useGemma, useGemmaSupported } from "../../../ai/gemma";
import { ChatTranscript } from "./components/ChatTranscript";
import { ChatComposer } from "./components/ChatComposer";
import type { ChatMessage } from "./types";

const buildPrompt = (history: ChatMessage[], userText: string): string => {
  const turns = history
    .filter((m) => m.text.length > 0)
    .map((m) => `${m.role === "user" ? "User" : "Assistant"}: ${m.text}`)
    .join("\n");
  return turns ? `${turns}\nUser: ${userText}` : userText;
};

const newId = () =>
  globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;

export const LLMChat = () => {
  const supported = useGemmaSupported();
  const { status, generate } = useGemma();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [error, setError] = useState<string | null>(null);

  const isBusy = status.kind === "loading" || status.kind === "busy";

  const handleSend = useCallback(
    (text: string, image: File | null) => {
      setError(null);
      const userMsg: ChatMessage = {
        id: newId(),
        role: "user",
        text,
        imageUrl: image ? URL.createObjectURL(image) : undefined,
      };
      const assistantId = newId();
      const assistantMsg: ChatMessage = {
        id: assistantId,
        role: "assistant",
        text: "",
      };
      setMessages((prev) => [...prev, userMsg, assistantMsg]);

      const history = messages;
      const prompt = buildPrompt(history, text || "(image attached)");

      void generate({
        prompt,
        image: image ?? undefined,
        onToken: (chunk) => {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId ? { ...m, text: m.text + chunk } : m,
            ),
          );
        },
      })
        .then((finalText) => {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId ? { ...m, text: m.text || finalText } : m,
            ),
          );
        })
        .catch((err: unknown) => {
          const message = err instanceof Error ? err.message : String(err);
          setError(message);
          setMessages((prev) => prev.filter((m) => m.id !== assistantId));
        });
    },
    [generate, messages],
  );

  const handleClear = () => {
    for (const m of messages) {
      if (m.imageUrl) URL.revokeObjectURL(m.imageUrl);
    }
    setMessages([]);
    setError(null);
  };

  if (!supported) {
    return (
      <Stack spacing={2}>
        <Typography variant="h5">LLM Chat (Gemma 4)</Typography>
        <Alert severity="info">
          Gemma 4 in-browser inference is not supported on mobile yet. Open this
          page on a desktop browser with WebGPU.
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack spacing={2} sx={{ height: { xs: "auto", md: "75vh" } }}>
      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", alignItems: "center" }}
      >
        <Typography variant="h5">LLM Chat (Gemma 4)</Typography>
        <Button
          size="small"
          onClick={handleClear}
          disabled={messages.length === 0}
        >
          Clear
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary">
        Debug playground for the in-browser Gemma 4 service. Model loads on
        first send (~1.5–2 GB, cached for next time). Status and unload live in
        the title bar.
      </Typography>
      {status.kind === "error" && (
        <Alert severity="error">{status.message}</Alert>
      )}
      {error && status.kind !== "error" && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 320,
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        <ChatTranscript messages={messages} />
      </Box>
      <ChatComposer disabled={isBusy} onSend={handleSend} />
    </Stack>
  );
};
